import { getDB } from "./schema";
import type { AgentRun, AgentMessage, AgentStatus } from "../types";

/**
 * Persist and retrieve agent runs from IndexedDB.
 */

export async function saveRun(run: AgentRun): Promise<void> {
  const db = await getDB();
  await db.put("runs", run);
}

export async function getRun(id: string): Promise<AgentRun | null> {
  const db = await getDB();
  const run = await db.get("runs", id);
  return (run as AgentRun) ?? null;
}

export async function updateRunStatus(
  id: string,
  status: AgentStatus,
  extra?: Partial<AgentRun>,
): Promise<void> {
  const db = await getDB();
  const run = await db.get("runs", id);
  if (!run) return;
  await db.put("runs", {
    ...run,
    status,
    ...extra,
    updatedAt: new Date().toISOString(),
  });
}

export async function listRuns(): Promise<AgentRun[]> {
  const db = await getDB();
  const runs = await db.getAllFromIndex("runs", "by-date");
  // Most recent first
  return (runs as AgentRun[]).reverse();
}

export async function deleteRun(id: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(
    ["runs", "messages", "conversations", "panelLayouts", "widgetContent", "panelViews"],
    "readwrite",
  );
  // Delete the run
  await tx.objectStore("runs").delete(id);
  // Delete conversation turns
  await tx.objectStore("conversations").delete(id);
  // Delete panel layout
  await tx.objectStore("panelLayouts").delete(id);
  // Delete all messages for this run
  const msgIndex = tx.objectStore("messages").index("by-run");
  let msgCursor = await msgIndex.openCursor(IDBKeyRange.only(id));
  while (msgCursor) {
    await msgCursor.delete();
    msgCursor = await msgCursor.continue();
  }
  // Delete all widgets for this run
  const widgetIndex = tx.objectStore("widgetContent").index("by-run");
  let widgetCursor = await widgetIndex.openCursor(IDBKeyRange.only(id));
  while (widgetCursor) {
    await widgetCursor.delete();
    widgetCursor = await widgetCursor.continue();
  }
  // Delete all views for this run
  const viewIndex = tx.objectStore("panelViews").index("by-run");
  let viewCursor = await viewIndex.openCursor(IDBKeyRange.only(id));
  while (viewCursor) {
    await viewCursor.delete();
    viewCursor = await viewCursor.continue();
  }
  await tx.done;
}

export async function getResumableRuns(): Promise<AgentRun[]> {
  const db = await getDB();
  const runs = await db.getAll("runs");
  return (runs as AgentRun[]).filter(
    (r) => r.status !== "done" && r.status !== "error" && r.status !== "idle",
  );
}

// ─── Messages ──────────────────────────────────────────

export async function appendMessage(message: AgentMessage): Promise<void> {
  const db = await getDB();
  await db.put("messages", message);
}

export async function getMessages(runId: string): Promise<AgentMessage[]> {
  const db = await getDB();
  const messages = await db.getAllFromIndex("messages", "by-run", runId);
  return (messages as AgentMessage[]).sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
}
